<template>
  <div class="action-config-item">
    <div class="mb-4">
      <label for="action-name" class="block text-sm font-semibold text-gray-700">Action Name</label>
      <el-input
        v-model="localStep.label"
        id="action-name"
        placeholder="Enter Action Name"
        clearable
        class="w-full rounded-md"
      />
    </div>
    <el-button
      type="text"
      icon="el-icon-s-tools"
      @click="openConfigureAndSendDocTemplate()"
      class="ml-1"
    >
      Configure
    </el-button>
    <!-- Footer Actions -->
    <div class="automation-footer-actions mt-4 flex justify-end gap-2">
      <el-button @click="cancelChanges">Cancel</el-button>
      <el-button type="primary" @click="saveChanges">Save</el-button>
    </div>
    <dialog-component
      :title="'Configure Users & Document Templates'"
      :visible="openDocumentTemplateDialog"
      @before-close="resetSendDocumentTemplate"
      :containerWidth="'49%'"
    >
      <div v-if="mapFieldsToDocTemplateModel">
        <el-table :data="getAllEntityFields(entityIDForDocTemp)" size="mini" border>
          <el-table-column label="Field" prop="label" width="200">
            <template slot-scope="{ row }">
              <div style="font-weight: 500; font-size: 12px; color: #030503">
                {{ row.label }}
              </div>
            </template>
          </el-table-column>
          <el-table-column label="Mapped with" prop="key" width="250">
            <template slot-scope="scope">
              <div class="d-flex">
                <el-select
                  multiple
                  v-model="
                    doc_template_configuration[0].fieldsMappedData[
                      'mapped/' + scope.row._id + '#' + scope.row.key
                    ]
                  "
                >
                  <el-option
                    v-for="(field, i) in elements[0].pages[0].fields"
                    :key="field + i"
                    :value="field.key"
                    :label="field.label"
                  ></el-option>
                </el-select>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <div class="custom-card mt-1 mb-1">
        <template v-if="doc_template_configuration.length && !mapFieldsToDocTemplateModel">
          <div v-for="(temp, index) in doc_template_configuration" :key="index" class="d-flex">
            <el-tooltip content="Select the document template." placement="top">
              <el-select
                class="mb-1 mt-1 fw-400 w-100"
                placeholder="Select Document Template"
                v-model="temp.doc_template_id"
                @change="handleDocTemplateChange"
                clearable
              >
                <el-option
                  v-for="(doctemp, index) in docTemplatesData"
                  :key="index + '_' + doctemp._id"
                  :label="doctemp.name"
                  :value="doctemp._id"
                ></el-option>
              </el-select>
            </el-tooltip>
            <el-tooltip
              content="Map current entity field values to the selected document template fields"
              placement="top"
            >
              <el-button
                @click="openDocumentTemplateFieldsConfig(temp, index)"
                type="text"
                class="ml-1"
              >
                Map values
                <i class="el-icon-info"></i>
              </el-button>
            </el-tooltip>
            <el-tooltip
              content="The document will be created and saved as a draft. You can send it whenever you'd like from the 'Doc Templates' section in the 'E-Signatures' tab."
              placement="top"
            >
              <el-checkbox
                type="text"
                class="ml-1 checkbox-center-right"
                v-model="temp.is_saved_as_draft"
              >
                Ready To Send
                <i class="el-icon-info"></i>
              </el-checkbox>
            </el-tooltip>
            <el-tooltip content="The action-triggered user will be the sender." placement="top">
              <el-checkbox
                type="text"
                class="ml-1 checkbox-center-right"
                v-model="temp.is_Action_Triggered_User_As_Sender"
              >
                Update Action User As Sender
                <i class="el-icon-info"></i>
              </el-checkbox>
            </el-tooltip>
            <el-tooltip
              content="eSign settings are not enabled for this application user. To send completion emails to both sender and receiver, please enable eSign settings."
              placement="top"
              v-if="temp.is_Action_Triggered_User_As_Sender"
            >
              <i
                class="el-icon-setting checkbox-center-right"
                @click="openSenderSettingsModalForDocTemp = true"
              ></i>
            </el-tooltip>
          </div>
        </template>
      </div>
      <div v-for="(temp, index) in doc_template_configuration" :key="index">
        <template v-if="showLimitedUsers">
          <el-tooltip
            content="Select the user to send the document template (receiver)"
            placement="left"
          >
            <div class="custom-card">
              <span v-if="showLimitedUsers" class="number-highlight">{{ index + 1 }}.</span>

              <el-select
                class="mb-1 mt-1 p-2 fw-500"
                clearable
                placeholder="Configure Users"
                :default-first-option="true"
                v-model="temp.selected_user"
              >
                <el-option
                  v-for="(user, userIndex) in selectMail"
                  :key="userIndex"
                  :label="getUserLabel(user)"
                  :value="user"
                />
              </el-select>
            </div>
          </el-tooltip>
        </template>
        <dialog-component
          title="Configure Settings For Application User"
          :visible="openSenderSettingsModalForDocTemp"
          @before-close="openSenderSettingsModalForDocTemp = false"
          :containerWidth="'49%'"
          v-if="temp.is_Action_Triggered_User_As_Sender"
        >
          <template>
            <div class="action-settings">
              <strong class="heading">Configure the settings for the action-triggered user:</strong>

              <div class="setting-option">
                <el-checkbox v-model="temp.is_recipient_signs_notify">
                  Send completion email to user
                </el-checkbox>
                <el-tooltip
                  content="Enable this option to send a completion email to the user once the document is completed."
                  placement="top"
                >
                  <i class="el-icon-info info-icon"></i>
                </el-tooltip>
              </div>

              <div class="setting-option">
                <el-checkbox v-model="temp.attach_pdf">Attach PDF to completion email</el-checkbox>
                <el-tooltip
                  content="Enable this option to attach the PDF to the completion email sent to the user."
                  placement="top"
                >
                  <i class="el-icon-info info-icon"></i>
                </el-tooltip>
              </div>
            </div>
          </template>
          <span slot="footer" class="dialog-footer">
            <el-button type="plain" @click="openSenderSettingsModalForDocTemp = false">
              Close
            </el-button>
            <el-button type="primary" @click="openSenderSettingsModalForDocTemp = false">
              Save Settings
            </el-button>
          </span>
        </dialog-component>
        <template v-else-if="showMultipleUsers">
          <div>
            <el-row
              class="custom-card mt-1"
              v-for="(user, userIndex) in temp.selected_users"
              :key="userIndex"
              :gutter="10"
            >
              <span v-if="user.user_type !== 'SENDER'" class="number-highlight">
                {{ userIndex + 1 + '.' }}
              </span>

              <el-col :span="8">
                <el-input
                  v-if="user.type === 'SENDER'"
                  v-model="user.email"
                  :disabled="true"
                ></el-input>
                <el-input
                  :disabled="user?.contact_type === entityIDForDocTemp && !!user.contact_id"
                  placeholder="Contact Details - email"
                  v-model="user.email"
                  v-if="
                    user?.contact_type === entityIDForDocTemp &&
                    user.type !== 'SENDER' &&
                    user.contact_id !== ''
                  "
                ></el-input>
                <el-select
                  v-if="
                    user.type !== 'SENDER' &&
                    (user?.contact_type !== entityIDForDocTemp ||
                      (user?.contact_type === entityIDForDocTemp && user.contact_id === ''))
                  "
                  v-model="user.contact_id"
                  placeholder="Send to"
                  @change="(changedValue) => getEntityDetails(entityIDForDocTemp, localStep)"
                  clearable
                  filterable
                >
                  <el-option-group label="Entity fields">
                    <el-option
                      v-for="(field, i) in getUserEntities(
                        getFieldsByType(['ENTITY'], allFieldsWithDataTable)
                      )"
                      :key="field.entity_id + '#' + i"
                      :value="field.key + '#' + user.name + '#' + user.contact_type"
                      :label="field.label"
                    />
                  </el-option-group>
                </el-select>
              </el-col>
              <el-col :span="8">
                <el-input :disabled="true" v-model="user.name"></el-input>
              </el-col>
              <el-col :span="8">
                <el-input :disabled="true" v-model="user.user_type"></el-input>
              </el-col>
            </el-row>
          </div>
        </template>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button
          v-if="!mapFieldsToDocTemplateModel"
          type="plain"
          @click="resetSendDocumentTemplate"
        >
          Close
        </el-button>
        <el-button
          v-if="mapFieldsToDocTemplateModel"
          type="plain"
          @click="resetMappingDocumentTemplateDialog"
        >
          Back To User Configuration
        </el-button>
        <el-button type="primary" @click="saveDocTemplateConfig">Save Configuration</el-button>
      </span>
    </dialog-component>
  </div>
</template>

<script>
  import { postAPICall } from '../../../../helpers/httpHelper';
  import { getStandardTemplate } from '../../../../repo/templatesRepo';
  import StepEditableMixin from './StepEditableMixin';
  import templateBuilderHelper from '../../../../mixins/templateBuilderHelper';
  export default {
    name: 'SendDocTemplateAction',
    props: {
      step: Object,
      currentEntity: Object,
      allEntities: Array,
    },
    mixins: [StepEditableMixin, templateBuilderHelper],
    data() {
      return {
        allFieldsWithDataTable: [],
        doc_template_configuration: [
          {
            selected_users: [
              {
                entity_id: '',
                name: '',
                user_type: '',
                contact_id: '',
              },
            ],
            doc_template_id: '',
            selected_entity: '',
            fieldsMappedData: {},
            is_saved_as_draft: false,
            is_Action_Triggered_User_As_Sender: false,
            //  mapped: [],
          },
        ],
        openDocumentTemplateDialog: false,
        mapFieldsToDocTemplateModel: false,
        loading: false,
        selectMail: [],
        LoadingText: '',
        entityIDForDocTemp: '',
        docTemplatesData: [],
        standardTemplate: null,
        multipleUsers: [],
        selectedUserInDocUsers: '',
        mappingIndex: -1,
      };
    },
    mounted() {
      this.allFieldsWithDataTable = this.getAllFieldsFromEntity(
        this.currentEntity,
        true,
        true,
        true
      );
      (this.allEntities || []).forEach((entity) => {
        this.actionEntityFields[entity._id] = this.getAllFieldsFromEntity(
          entity,
          false,
          false,
          true
        );
      });
    },
    computed: {
      showLimitedUsers() {
        return (
          this.doc_template_configuration[0] &&
          this.doc_template_configuration[0]?.selected_users &&
          this.doc_template_configuration[0]?.selected_users?.length <= 2 &&
          this.doc_template_configuration[0]?.doc_template_id &&
          !this.mapFieldsToDocTemplateModel
        );
      },
      showMultipleUsers() {
        return (
          this.doc_template_configuration[0] &&
          this.doc_template_configuration[0]?.selected_users &&
          this.doc_template_configuration[0]?.selected_users?.length > 2 &&
          this.doc_template_configuration[0]?.doc_template_id &&
          !this.mapFieldsToDocTemplateModel
        );
      },
    },
    methods: {
      async openConfigureAndSendDocTemplate() {
        this.doc_template_configuration =
          this.localStep?.doc_template || this.doc_template_configuration;
        if (!this.doc_template_configuration[0].fieldsMappedData) {
          this.doc_template_configuration[0].fieldsMappedData = {};
        }
        this.openDocumentTemplateDialog = true;
        await this.fetchDocTemplatesUsingEntity();
      },
      resetSendDocumentTemplate() {
        this.openDocumentTemplateDialog = false;
        this.mapFieldsToDocTemplateModel = false;
      },
      async fetchDocTemplatesUsingEntity() {
        try {
          this.selectMail = [];
          this.loading = true;
          this.LoadingText = 'Please wait, fetching document templates...';
          this.entityIDForDocTemp = this.currentEntity._id;
          let response = await postAPICall('GET', `/doc_temp/${this.entityIDForDocTemp}`);
          this.docTemplatesData = response.data;
          // let standardTemplateData = await axios.get("/get-standard-template");
          // let standardTemplate = standardTemplateData.data.data;
          if (!this.standardTemplate) this.standardTemplate = await getStandardTemplate();
          if (!this.selectMail.includes(`${this.standardTemplate._id}#email`)) {
            this.selectMail.push(`${this.standardTemplate._id}#email`);
          }
          this.loading = false;
        } catch (err) {
          this.loading = false;
          console.log(err);
        }
      },
      async handleDocTemplateChange(temp) {
        try {
          let data = this.docTemplatesData.find((x) => x._id == temp);
          let userContactType = data.document_users.filter((e) => {
            return e?.contact_type == this.entityIDForDocTemp;
          });
          this.selectMail = [];
          if (data && data?.document_users?.length) {
            this.doc_template_configuration[0].selected_users = data.document_users.map(
              ({ user_type, name, contact_type, email, type }) => ({
                user_type,
                name,
                contact_type,
                email,
                type,
              })
            );
            if (userContactType?.length !== 0 && userContactType.length === 1) {
              if (!this.standardTemplate) this.standardTemplate = await getStandardTemplate();
              this.doc_template_configuration[0].selected_users =
                this.doc_template_configuration[0].selected_users.map((user) => {
                  if (user.contact_type === this.entityIDForDocTemp) {
                    this.selectMail.push(`${this.standardTemplate._id}#email`);
                    return {
                      ...user,
                      email: 'Contact Details - email',
                      contact_id: `${this.selectMail[0]}#${this.entityIDForDocTemp}`,
                    };
                  }
                  return user;
                });
            } else if (userContactType?.length !== 0 && userContactType.length > 1) {
              if (!this.standardTemplate) this.standardTemplate = await getStandardTemplate();
              let firstAssigned = false;
              this.doc_template_configuration[0].selected_users =
                this.doc_template_configuration[0].selected_users.map((user) => {
                  if (user.contact_type === this.entityIDForDocTemp) {
                    if (!firstAssigned) {
                      this.selectMail.push(`${this.standardTemplate._id}#email`);
                      firstAssigned = true;
                      return {
                        ...user,
                        email: 'Contact Details - email',
                        contact_id: `${this.selectMail[0]}#${this.entityIDForDocTemp}`,
                      };
                    } else {
                      return {
                        ...user,
                        email: '',
                        contact_id: '',
                      };
                    }
                  }
                  return user;
                });
            }
          }
          if (this.multipleUsers.length <= 2) {
            if (!this.standardTemplate) this.standardTemplate = await getStandardTemplate();
            if (this.selectMail.length === 0) {
              this.selectMail.push(`${this.standardTemplate._id}#email`);
            }
            let mail = this.selectMail[0] ? this.selectMail[0] : this.selectedUserInDocUsers;
            this.doc_template_configuration[0].selected_user = this.entityIDForDocTemp + '#' + mail;
          }
          this.mapFieldsToDocTemplateModel = false;
        } catch (err) {
          console.log(err);
        }
      },
      saveDocTemplateConfig() {
        this.$set(this.localStep, 'doc_template', this.doc_template_configuration);
        this.resetSendDocumentTemplate();
      },
      async openDocumentTemplateFieldsConfig(temp, i) {
        try {
          this.mappingIndex = i;
          if (!temp?.doc_template_id) {
            this.$notify.warning({
              title: 'Warning',
              message: 'Please Select Document Template',
            });
            return;
          }
          this.elements = this.docTemplatesData.filter((e) => e._id === temp.doc_template_id);
          this.mapFieldsToDocTemplateModel = true;
        } catch (err) {
          console.log(err);
        }
      },
    },
  };
</script>
<style scoped>
  .action-config-item {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    font-family:
      'Inter',
      system-ui,
      -apple-system,
      BlinkMacSystemFont,
      'Segoe UI',
      Roboto,
      'Helvetica Neue',
      Arial,
      sans-serif;
  }

  .action-config-item .el-button {
    font-weight: 500;
    font-size: 14px;
    padding: 4px 12px;
  }

  .custom-card {
    background-color: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 0.75rem 1rem;
    margin-bottom: 0.5rem;
  }

  .number-highlight {
    font-weight: 600;
    color: #3b82f6;
    margin-right: 8px;
  }

  .el-table {
    width: 100%;
    font-size: 12px;
    border-radius: 6px;
    overflow: hidden;
  }

  .el-table th {
    background-color: #f3f4f6;
    color: #374151;
    font-weight: 600;
    font-size: 12px;
  }

  .el-table td {
    font-size: 12px;
    color: #111827;
  }

  .el-select {
    width: 100%;
    border-radius: 6px;
  }

  .el-select .el-input__inner {
    font-size: 13px;
    padding: 0.35rem 0.75rem;
  }

  .el-select-dropdown__item:hover {
    background-color: #eff6ff;
  }

  .el-tooltip i.info-icon {
    font-size: 14px;
    color: #6b7280;
    margin-left: 4px;
  }

  .checkbox-center-right {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .action-settings {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    font-size: 13px;
  }

  .action-settings .heading {
    font-weight: 600;
    margin-bottom: 0.5rem;
  }

  .setting-option {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .dialog-footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 12px 0;
    border-top: 1px solid #e5e7eb;
  }

  .d-flex {
    display: flex;
    gap: 0.5rem;
  }

  .mt-1 {
    margin-top: 0.5rem;
  }

  .mb-1 {
    margin-bottom: 0.5rem;
  }

  .ml-1 {
    margin-left: 0.5rem;
  }

  .fw-400 {
    font-weight: 400;
  }

  .fw-500 {
    font-weight: 500;
  }

  .p-2 {
    padding: 0.5rem;
  }
</style>
